<script setup>
import { ref, computed, watch } from "vue";
import { NModal, NButton, NSpace, NCard, NRadioGroup, NRadio } from "naive-ui";

const props = defineProps({
  show: Boolean,
});

const emit = defineEmits(["update:show", "language-selected"]);

const selectedLanguage = ref("english");

// Computed property to determine if we should actually show the modal
const shouldShowModal = computed(() => {
  // Don't show the language selector during OAuth callback process
  if (window.location.pathname.includes('/auth/oauth') || 
      window.location.pathname.includes('/oauth-callback')) {
    return false;
  }
  return props.show;
});

// Watch for changes in shouldShowModal and emit an update if needed
watch(shouldShowModal, (newValue) => {
  if (!newValue && props.show) {
    emit("update:show", false);
  }
});

const languages = [
  { label: "English", value: "english" },
  { label: "हिंदी (Hindi)", value: "hindi" },
  { label: "தமிழ் (Tamil)", value: "tamil" },
  { label: "తెలుగు (Telugu)", value: "telugu" },
  { label: "ಕನ್ನಡ (Kannada)", value: "kannada" },
  { label: "മലയാളം (Malayalam)", value: "malayalam" },
  { label: "ਪੰਜਾਬੀ (Punjabi)", value: "punjabi" },
  { label: "ગુજરાતી (Gujarati)", value: "gujarati" },
  { label: "मराठी (Marathi)", value: "marathi" },
  { label: "বাংলা (Bengali)", value: "bengali" },
  { label: "اُردُو (Urdu)", value: "urdu" }
];

const saveLanguagePreference = () => {
  emit("language-selected", selectedLanguage.value);
  emit("update:show", false);
};
</script>

<template>
  <NModal
    v-model:show="shouldShowModal"
    preset="card"
    title="Select Your Preferred Language"
    :closable="false"
    :mask-closable="false"
    style="width: 90%; max-width: 550px"
  >
    <div class="language-modal-content">
      <p class="language-intro">
        Welcome! Please select your preferred language for the best experience.
        You can change this later in your settings.
      </p>

      <NRadioGroup v-model:value="selectedLanguage" name="language">
        <NSpace wrap :size="[12, 12]">
          <NCard
            v-for="lang in languages"
            :key="lang.value"
            :class="[
              'language-card',
              selectedLanguage === lang.value ? 'selected' : '',
            ]"
            hoverable
            @click="selectedLanguage = lang.value"
            style="width: 140px"
          >
            <NRadio :value="lang.value">
              <span style="white-space: nowrap">{{ lang.label }}</span>
            </NRadio>
          </NCard>
        </NSpace>
      </NRadioGroup>
    </div>

    <template #footer>
      <div class="language-modal-footer">
        <NButton type="primary" size="large" @click="saveLanguagePreference">
          Continue
        </NButton>
      </div>
    </template>
  </NModal>
</template>

<style scoped>
.language-modal-content {
  padding: 0.5rem 0;
}

.language-intro {
  margin-bottom: 1rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #555;
}

.language-card {
  transition: all 0.2s ease;
  border: 2px solid transparent;
  margin-bottom: 0.5rem;
}

.language-card.selected {
  border-color: #2080f0;
  background-color: rgba(32, 128, 240, 0.1);
}

.language-modal-footer {
  display: flex;
  justify-content: center;
  padding-top: 1rem;
}

:deep(.n-modal-container) {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
}
</style>